"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

/**
 * Self-service claim withdrawal for the claimant, mirrors the Dialog-confirm
 * pattern UnclaimFirmButton.tsx uses for its own self-service action —
 * except withdrawing your own pending request isn't destructive the way
 * giving up a firm you already manage is, so this stays `outline`, not
 * `destructive`.
 */
export function WithdrawClaimButton({ claimId }: { claimId: string }) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);

  async function handleWithdraw() {
    setPending(true);
    try {
      const res = await fetch(`/api/claims/${claimId}`, { method: "DELETE" });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        toast.error(data?.error?.message ?? "Could not withdraw this claim.");
        return;
      }
      toast.success("Your claim has been withdrawn.");
      setOpen(false);
      // Server Component page re-evaluates the pending claim lookup and the
      // panel falls through to the "eligible" state.
      router.refresh();
    } catch {
      toast.error("Could not withdraw this claim.");
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !pending && setOpen(next)}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline">
          Withdraw this claim
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Withdraw this claim?</DialogTitle>
          <DialogDescription>
            This withdraws your request to manage this firm. It won&apos;t be
            reviewed by an administrator. You can claim this firm — or a
            different one — again later if you change your mind.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleWithdraw}
            disabled={pending}
          >
            {pending ? "Withdrawing…" : "Withdraw this claim"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
